"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SecureScaleCustodyClassification = exports.StoragePressureUploadRequest = exports.StoragePressureRecord = exports.SecureScaleStorageStatus = exports.SecureScaleStorageUsage = exports.SecureScaleStorageConfiguration = exports.SecureScaleStorageConfig = exports.SecureScaleStorageResource = void 0;
const zod_1 = require("zod");
const Identifier = zod_1.z
    .string()
    .min(1)
    .max(128)
    .refine((value) => value.trim().length > 0);
const Bytes = zod_1.z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const PositiveBytes = Bytes.refine((value) => value > 0);
/** Trusted deployment resource, never inferred from downloaded tenant data. */
exports.SecureScaleStorageResource = zod_1.z
    .object({
    kind: zod_1.z.enum(['terminal', 'link']),
    id: Identifier,
})
    .strict();
/** D21: separate engineer-sized pools; no borrowing and no hardware defaults. */
exports.SecureScaleStorageConfig = zod_1.z
    .object({
    schemaVersion: zod_1.z.literal(1),
    revision: zod_1.z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    resource: exports.SecureScaleStorageResource,
    accountingBasis: zod_1.z.literal('logical-retained-utf8'),
    routineQuotaBytes: PositiveBytes,
    protectedReserveBytes: PositiveBytes,
})
    .strict();
exports.SecureScaleStorageConfiguration = zod_1.z.discriminatedUnion('state', [
    zod_1.z.object({ state: zod_1.z.literal('configured'), config: exports.SecureScaleStorageConfig }).strict(),
    zod_1.z.object({ state: zod_1.z.literal('unconfigured'), config: zod_1.z.null() }).strict(),
    zod_1.z.object({ state: zod_1.z.literal('unavailable'), config: zod_1.z.null() }).strict(),
]);
/** Every retained body copy and immutable custody metadata is charged once per copy.
 * Metadata bytes are a subset of the two pool charges. Replicas and staging are
 * outside those pools and reported separately; null means not measured.
 * Unattributed retained custody is conservatively charged to the protected pool.
 * These numbers are not SQL allocation, filesystem free bytes or RAM usage.
 */
exports.SecureScaleStorageUsage = zod_1.z
    .object({
    routineBytes: Bytes,
    protectedBytes: Bytes,
    metadataBytes: Bytes,
    replicaBytes: Bytes.nullable(),
    stagingBytes: Bytes.nullable(),
    unattributedBytes: Bytes.nullable(),
})
    .strict()
    .superRefine((value, ctx) => {
    const charged = value.routineBytes + value.protectedBytes;
    if (!Number.isSafeInteger(charged) ||
        value.metadataBytes > charged ||
        (value.unattributedBytes !== null && value.unattributedBytes > value.protectedBytes))
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'Invalid retained metadata charge' });
});
const Admission = zod_1.z.enum(['ready', 'full', 'unknown']);
exports.SecureScaleStorageStatus = zod_1.z
    .object({
    schemaVersion: zod_1.z.literal(1),
    resource: exports.SecureScaleStorageResource,
    observedAt: zod_1.z.string().datetime(),
    configuration: exports.SecureScaleStorageConfiguration,
    usage: exports.SecureScaleStorageUsage.nullable(),
    /** Ready means some logical headroom; each incoming record still needs a fit check. */
    admission: zod_1.z.object({ routine: Admission, protected: Admission }).strict(),
    physical: zod_1.z.object({ available: zod_1.z.boolean(), freeBytes: Bytes.nullable() }).strict(),
    discarded: zod_1.z
        .object({
        pendingSummaries: Bytes,
        droppedRecordCount: Bytes,
        firstCapturedAt: zod_1.z.string().datetime().nullable(),
        lastCapturedAt: zod_1.z.string().datetime().nullable(),
    })
        .strict()
        .nullable(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.physical.available !== (value.physical.freeBytes !== null))
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['physical'],
            message: 'Unknown physical capacity cannot be reported as measured',
        });
    const configured = value.configuration.state === 'configured' ? value.configuration.config : null;
    if (configured &&
        (configured.resource.kind !== value.resource.kind ||
            configured.resource.id !== value.resource.id))
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['configuration'],
            message: 'Storage config belongs to another resource',
        });
    if (!configured || value.usage === null) {
        if (value.admission.routine !== 'unknown' || value.admission.protected !== 'unknown')
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['admission'],
                message: 'Unknown configuration or usage cannot authorize admission',
            });
    }
    else {
        if (value.admission.routine !==
            (value.usage.routineBytes >= configured.routineQuotaBytes ? 'full' : 'ready') ||
            value.admission.protected !==
                (value.usage.protectedBytes >= configured.protectedReserveBytes ? 'full' : 'ready'))
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['admission'],
                message: 'Pool status does not match its configured logical charge',
            });
    }
    const gap = value.discarded;
    if (gap) {
        const hasBounds = gap.firstCapturedAt !== null && gap.lastCapturedAt !== null;
        if (gap.droppedRecordCount > 0 !== hasBounds ||
            (gap.droppedRecordCount === 0 &&
                (gap.firstCapturedAt !== null || gap.lastCapturedAt !== null)) ||
            (hasBounds && Date.parse(gap.firstCapturedAt) > Date.parse(gap.lastCapturedAt)))
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['discarded'],
                message: 'Discard count and time bounds must agree',
            });
    }
});
/** Immutable authenticated diagnostic record; never a weighing or learning grant. */
exports.StoragePressureRecord = zod_1.z
    .object({
    recordType: zod_1.z.literal('storage_pressure'),
    schemaVersion: zod_1.z.literal(1),
    clientUuid: zod_1.z.string().uuid(),
    sourceComponent: zod_1.z.enum(['sco', 'link']),
    emitterId: Identifier,
    observedScope: zod_1.z.object({ tenantId: zod_1.z.string().uuid(), storeId: Identifier }).strict(),
    observedAt: zod_1.z.string().datetime(),
    status: exports.SecureScaleStorageStatus,
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.emitterId !== value.status.resource.id ||
        (value.sourceComponent === 'sco' ? 'terminal' : 'link') !== value.status.resource.kind ||
        value.observedAt !== value.status.observedAt)
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'Emitter, resource and observation identity must agree',
        });
});
exports.StoragePressureUploadRequest = zod_1.z
    .object({ records: zod_1.z.array(exports.StoragePressureRecord).min(1).max(64) })
    .strict();
/** Immutable source decision before custody handoff; absent declaration is protected.
 * Routine is never inferred from audited=false: audit selection must already be finalized.
 */
exports.SecureScaleCustodyClassification = zod_1.z.discriminatedUnion('class', [
    zod_1.z
        .object({
        schemaVersion: zod_1.z.literal(1),
        class: zod_1.z.literal('routine'),
        auditSelectionFinalized: zod_1.z.literal(true),
    })
        .strict(),
    zod_1.z.object({ schemaVersion: zod_1.z.literal(1), class: zod_1.z.literal('protected') }).strict(),
]);
//# sourceMappingURL=storage-pressure.js.map